import { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { openFields, sealFields } from '../../utils/spaceKeys';
import { titleOf } from '../nucleus/GraphModel';
import { PROPOSAL_WAITING, QUESTION_WAITING } from '../../nucleus/attention';
import { useMarkAttentionStale } from '../../contexts/NotificationContext';

// THE GRAPH SECTION'S DATA — what the four pages read and write, every call
// branching once on the space (personal /nodes/…, a group /groups/<id>/…).
// The node model itself (loaders, filters, sorts, saveNode, relate, versions)
// is the one in pages/nucleus/GraphModel.js; this file holds what that one
// keeps private or never had: opening sealed fields, a node's links, making
// and trashing a node, the whole trash, the review queue and schema writes —
// each THROWING on failure, so the one action runner can toast it.

// ── the view ───────────────────────────────────────────────────────────────
export function readPref(key, fallback) {
    try { const v = localStorage.getItem(key); return v == null ? fallback : JSON.parse(v); } catch { return fallback; }
}
export function writePref(key, v) {
    try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* private mode */ }
}

export const words = (s) => String(s || '').replace(/_/g, ' ');

// ── a node ─────────────────────────────────────────────────────────────────

/** The node's fields, opened where your keys reach: in a group, the group key
 *  (every generation you hold), then your own; in your own space, your key and
 *  the node's v3 data key (utils/spaceKeys.js). */
export function readable(node, encKeys, group = null) {
    return openFields(node, { group, encKeys });
}

/** A node's links, both ways, through the space's own route. */
export async function relationsOf(node, group) {
    const rels = group
        // the real belongs_to links are their own schema, asked for beside the rest
        ? (await Promise.all([
            api.getGroupRelationships(group, { from_node_id: node.id }),
            api.getGroupRelationships(group, { to_node_id: node.id }),
            api.getGroupRelationships(group, { from_node_id: node.id, relationship_type: 'belongs_to' }),
            api.getGroupRelationships(group, { to_node_id: node.id, relationship_type: 'belongs_to' }),
        ])).flat()
        : await api.getNodeRelationships(node.id);
    const seen = new Set();
    return (rels || []).filter((r) => r && !seen.has(r.id) && seen.add(r.id)).map((r) => {
        const outgoing = r.from_node_id === node.id;
        const other = outgoing ? r.to_node : r.from_node;
        return {
            id: r.id, type: r.relationship_type || 'relates_to', outgoing,
            other: outgoing ? r.to_node_id : r.from_node_id, otherTitle: other?.title || 'A node',
            // a group's link is a node: its own `can` (and author), when the row carries them
            ...(r.can ? { can: r.can } : {}),
            ...(r.created_by_user_id ? { created_by_user_id: r.created_by_user_id } : {}),
        };
    });
}

/** A new node, in the space you are in; a schema's encrypted fields are sealed
 *  first, in a group to the group's key so every member can open them.
 *  `existed`: an exact repeat is not a second node — the server answered with
 *  the one already there, and the caller must say so rather than pass it off as new. */
export async function createNode(group, { title, schemaId, value }, encKeys, { groupName = null } = {}) {
    let v = value || {};
    if (schemaId) {
        const s = await api.getSchema(schemaId).catch(() => null);
        const enc = (s?.schema || s)?.encrypted_fields || [];
        if (enc.length) v = await sealFields(v, enc, { group, groupName, encKeys });
    }
    const body = { title, value_json: v, ...(schemaId ? { schema_id: schemaId } : {}) };
    const r = group ? await api.createGroupNode(group, body) : await api.createNode(title, v, schemaId || null);
    const node = r?.node || r;
    return node && r?.message === 'Node already exists' ? { ...node, existed: true } : node;
}

/** To the trash — restorable, with its links, until it is purged. */
export const trashNode = (group, id) => (group ? api.deleteGroupNode(group, id) : api.deleteNode(id));

/** A file node's short-lived download address, through the space's route. */
export async function downloadNode(group, node) {
    const r = group ? await api.request(`/groups/${group}/nodes/${node.id}/download`) : await api.getNodeDownloadUrl(node.id);
    if (!r?.download_url) throw new Error('It has no file to download.');
    window.open(r.download_url, '_blank', 'noopener');
}

/** Who can see a node you own: the realms (and people) it reaches. Personal only. */
export async function sharedWith(id) {
    try {
        const r = await api.request(`/nodes/${id}/shared-with`);
        return { groups: r?.grouped || [], rows: r?.shared_with || [] };
    } catch (e) {
        if (e?.status === 403) return { groups: [], rows: [] };
        throw e;
    }
}

// ── the trash ──────────────────────────────────────────────────────────────
const TRASH_PAGE = 200;

/** One page of the space's trash: { nodes, total, autoPurge }. */
export async function loadTrash(group, offset = 0) {
    const r = await api.getTrashedNodes({ limit: TRASH_PAGE, offset, ...(group ? { group_id: group } : {}) });
    const nodes = r?.nodes || [];
    return { nodes, total: typeof r?.total === 'number' ? r.total : nodes.length, autoPurge: !!r?.auto_purge_enabled };
}

export const restoreNode = (group, id, opts) => (group ? api.restoreGroupNode(group, id, opts) : api.restoreNode(id, opts));
export const purgeNode = (group, id) => (group ? api.purgeGroupNode(group, id) : api.purgeNode(id));

/** Empty the whole trash, every page of it: { done, failed, first } (first: the first failure's words). */
export async function emptyTrash(group) {
    let done = 0;
    let failed = 0;
    let first = null;
    // purged rows leave the list, so the next page always starts where the
    // failures end — read past what stayed, never over it
    for (let guard = 0; guard < 50; guard += 1) {
        const { nodes } = await loadTrash(group, failed);
        if (!nodes.length) break;
        if (!group) {
            // A PAGE PER REQUEST. One purge per node hit the 60-a-minute write limit
            // after about sixty, and the rest "couldn't be deleted".
            const r = await api.purgeTrashed(nodes.map((n) => n.id));
            done += (r?.purged || []).length;
            const fails = r?.failed || [];
            failed += fails.length;
            if (fails.length && !first) {
                const n = nodes.find((x) => String(x.id) === String(fails[0].id));
                first = `“${titleOf(n || {})}”: ${fails[0].message || 'it stayed'}`;
            }
        } else {
            for (const n of nodes) {
                try {
                    await purgeNode(group, n.id);
                    done += 1;
                } catch (e) {
                    failed += 1;
                    if (!first) first = `“${titleOf(n)}”: ${e?.message || 'it stayed'}`;
                }
            }
        }
        if (nodes.length < TRASH_PAGE) break;
    }
    return { done, failed, first };
}

// ── the review queue ───────────────────────────────────────────────────────
// The per-proposal routes (/proposals/<id>/approve|apply|reject|defer|
// edit-confirm) are group-aware on the server: any member of the proposal's
// group may act on it (proposals_v2 _get_proposal_or_404). Only the list has a
// /groups/<id>/proposals twin. Questions have no group route at all.

const REVIEW_PAGE = 200;
const statusOf = (x) => String(x?.status || 'PENDING').toUpperCase();

export const PROPOSAL_TABS = [
    { id: 'waiting', label: 'Waiting', has: (s) => PROPOSAL_WAITING.includes(s) },
    { id: 'later', label: 'Later', has: (s) => s === 'DEFERRED' },
    { id: 'approved', label: 'Approved', has: (s) => ['APPROVED', 'EDIT_CONFIRMED'].includes(s) },
    { id: 'applied', label: 'Applied', has: (s) => s === 'APPLIED' },
    // a failed apply is not a rejection: it was approved, wrote nothing, and can be retried
    { id: 'failed', label: 'Didn’t apply', has: (s) => s === 'FAILED' },
    { id: 'rejected', label: 'Rejected', has: (s) => s === 'REJECTED' },
    { id: 'archived', label: 'Archived', has: (s) => ['ARCHIVED', 'CANCELLED'].includes(s) },
];
export const QUESTION_TABS = [
    { id: 'waiting', label: 'Waiting', has: (s) => QUESTION_WAITING.includes(s) },
    { id: 'later', label: 'Later', has: (s) => s === 'DEFERRED' },
    { id: 'answered', label: 'Answered', has: (s) => s === 'ANSWERED' },
    { id: 'dismissed', label: 'Dismissed', has: (s) => s === 'DISMISSED' },
];

/** The edits a person actually made in a proposal editor: a field equal to what the
 *  editor showed is not an edit, and an emptied description is left out rather than
 *  written over the proposal's own. */
export function changedFields(p, { title, detail, value, shown }) {
    const edited = {};
    const t = (title || '').trim();
    if (t && t !== (p.title || '').trim()) edited.title = t;
    const d = (detail || '').trim();
    if (d && d !== (p.detail || '').trim()) edited.description = detail;
    if (value && Object.keys(value).length && JSON.stringify(value) !== JSON.stringify(shown || {})) edited.value_json = value;
    return edited;
}

export function proposalOf(p) {
    return {
        id: String(p.id), kind: 'proposal', status: statusOf(p),
        title: p.title || words(p.proposal_type) || 'Proposal',
        detail: p.description || p.rationale || null,
        // a group's proposal says whether the viewer may answer it (their realm's
        // verb on its target); undefined in your own space and before the backend says
        canAnswer: typeof p.can_answer === 'boolean' ? p.can_answer : undefined,
        app: p.app_name || null,
        type: p.type || p.schema_type || null,
        at: p.created_at,
        // the version the person is shown: approving sends it back, and the server
        // refuses if the app changed the proposal after it was loaded
        seen: p.updated_at || null,
        updated: p.updated_at || null,
        steering: p.steering_question || null,
        deferred: Number(p.deferred_count) || 0,
        rejection: p.rejection_reason || null,
        appId: p.app_id || null,
        // a plugin run's proposal: { kind: 'plugin', plugin: {id, name}, tool: {id, name}, run_id }
        source: p.source && typeof p.source === 'object' ? p.source : null,
        rootNode: p.root_node_id || null,
        tags: Array.isArray(p.tags) ? p.tags.map(String) : [],
    };
}

export function questionOf(q) {
    const ctx = q.context_data || {};
    return {
        id: String(q.id), kind: 'question', status: statusOf(q), text: q.question_text || 'A question', type: q.question_type,
        options: (ctx.options || ctx.choices || []).map(String), at: q.created_at, answer: q.answer_text || null, app: q.app_name || null,
    };
}

// One proposal as the inspector shows it. edit_value is exactly what a value edit
// replaces, worked out by the server where the edit is applied (the first create,
// merge or update it lands on); `changes` is an update's old -> new, field by field.
export async function readProposal(p) {
    const j = await api.getProposal(p.id);
    const d = j?.proposal || j || {};
    return { value: d.edit_value || {}, changes: Array.isArray(d.changes) ? d.changes : null };
}

/** What an applied proposal wrote, in words. */
export function appliedWords(r, p) {
    const nodes = r?.nodes_created?.length || 0;
    const rels = r?.relationships_created?.length || 0;
    // A failed apply wrote nothing (it is all or nothing). Said as a failure — the
    // action runner shows a throw as an error — never as "Applied with an error".
    if (r?.status === 'FAILED' || r?.errors?.length) throw new Error(`“${p.title}” didn’t apply: ${r?.errors?.[0] || 'it was refused'}`);
    if (!nodes && !rels) return `“${p.title}” applied. It wrote nothing.`;
    return `“${p.title}” is in the graph.`;
}

export function useReviewData(group = null) {
    const [proposals, setProposals] = useState(null);
    const [total, setTotal] = useState(0);
    const [questions, setQuestions] = useState(null);
    // Answering here changes what waits for the person: the section's Review
    // badge is read again now, not up to a minute later.
    const stale = useMarkAttentionStale();
    const answers = (fn) => async (...args) => { try { return await fn(...args); } finally { stale(); } };

    const page = useCallback((offset) => {
        const q = { limit: REVIEW_PAGE, offset };
        return group ? api.request(`/groups/${group}/proposals?${new URLSearchParams(q)}`) : api.getProposals(q);
    }, [group]);

    const load = useCallback(async () => {
        const [p, q] = await Promise.all([
            page(0).catch(() => null),
            group ? Promise.resolve({ questions: [] }) : api.getQuestions({ limit: 200 }).catch(() => null),
        ]);
        const rows = (p?.proposals || p?.items || []).filter((x) => x.id).map(proposalOf);
        setProposals(rows);
        setTotal(typeof p?.total === 'number' ? p.total : rows.length);
        setQuestions((q?.questions || []).filter((x) => x.id).map(questionOf));
    }, [page, group]);

    // a new space starts clean: never one space's queue under another
    useEffect(() => { setProposals(null); setQuestions(null); load(); }, [load]);

    const more = async () => {
        const p = await page(proposals?.length || 0);
        const rows = (p?.proposals || p?.items || []).filter((x) => x.id).map(proposalOf);
        setProposals((prev) => { const seen = new Set((prev || []).map((x) => x.id)); return [...(prev || []), ...rows.filter((x) => !seen.has(x.id))]; });
        if (typeof p?.total === 'number') setTotal(p.total);
    };
    const mark = (p, status) => setProposals((xs) => (xs || []).map((x) => (x.id === p.id ? { ...x, status } : x)));
    const markQ = (q, status, answer) => setQuestions((xs) => (xs || []).map((x) => (x.id === q.id ? { ...x, status, ...(answer ? { answer } : {}) } : x)));

    return {
        proposals, questions, total, load, more,
        /** Approve, then apply: the proposal reaches the graph. Returns the words to say. */
        approve: answers(async (p) => {
            await api.approveProposal(p.id, undefined, { seenUpdatedAt: p.seen });
            mark(p, 'APPROVED');
            const r = await api.applyProposal(p.id);
            mark(p, r?.status || 'APPLIED');
            return appliedWords(r, p);
        }),
        apply: answers(async (p) => {
            const r = await api.applyProposal(p.id);
            mark(p, r?.status || 'APPLIED');
            return appliedWords(r, p);
        }),
        /** A failed apply, again: back to approved, then apply. */
        retry: answers(async (p) => {
            await api.approveProposal(p.id);
            mark(p, 'APPROVED');
            const r = await api.applyProposal(p.id);
            mark(p, r?.status || 'APPLIED');
            return appliedWords(r, p);
        }),
        reject: answers(async (p, reason) => { await api.rejectProposal(p.id, reason); mark(p, 'REJECTED'); }),
        later: answers(async (p) => {
            const r = await api.deferProposal(p.id);
            mark(p, r?.auto_archived ? 'ARCHIVED' : 'DEFERRED');
            return r?.auto_archived ? 'Archived after three deferrals.' : 'For later.';
        }),
        /** Edit + confirm, then apply. Only what you changed is sent: the fields
         *  start from what the proposal shows (a placeholder title, its own value),
         *  and sent untouched the server read them as an edit. */
        editConfirm: answers(async (p, { title, detail, value, shown }) => {
            const edited = changedFields(p, { title, detail, value, shown });
            await api.editConfirmProposal(p.id, { edited_content: edited, ...(p.seen ? { seen_updated_at: p.seen } : {}) });
            mark(p, 'EDIT_CONFIRMED');
            const r = await api.applyProposal(p.id);
            mark(p, r?.status || 'APPLIED');
            return appliedWords(r, p);
        }),
        /** What a value edit would replace: the fields of the node the edit lands on. */
        detail: async (p) => (await readProposal(p)).value,
        /** …and, for an update, what it changes: [{field, old, new}] (null for any other). */
        read: readProposal,
        answer: answers(async (q, text) => { await api.answerQuestion(q.id, text, {}); markQ(q, 'ANSWERED', text); }),
        dismiss: answers(async (q) => { await api.dismissQuestion(q.id); markQ(q, 'DISMISSED'); }),
        deferQ: answers(async (q) => { await api.deferQuestion(q.id); markQ(q, 'DEFERRED'); }),
    };
}

// ── schemas ────────────────────────────────────────────────────────────────

function schemaRowOf(s) {
    return {
        id: s.id, raw: s, name: s.schema_name || s.name || 'schema', version: s.version ?? null, category: s.category || null,
        description: s.description || '', encrypted: s.encrypted_fields || [], json: JSON.stringify(s.json_schema || {}, null, 2),
        apps: (s.apps || []).length, system: !!s.is_system, ownerId: s.owner_id || null, groupId: s.group_id || null,
        scope: s.scope || 'public', parent: s.parent_schema?.schema_name || null,
        children: (s.extended_by || []).map((c) => c.schema_name || c.name || c).filter(Boolean),
        fields: Object.keys(s.json_schema?.properties || {}),
    };
}

/** Who may do what to a schema — prod's canManageSchema / canVersionSchema. */
export function rightsOf(s, { userId, groups = [] }) {
    if (!s || s.system) return { manage: false, version: false };
    // a group's schema is its admins' to change: a new version too
    if (s.groupId) {
        const g = groups.find((x) => x.id === s.groupId);
        const admin = g?.my_role === 'admin';
        return { manage: admin, version: admin };
    }
    const mine = !!userId && String(s.ownerId) === String(userId);
    return { manage: mine, version: mine };
}

const SCHEMA_PAGE = 200;

/** The space's schemas, read: in a group, the group's view — system, the group's
 *  own and those its nodes use, never your personal schemas or another group's
 *  (GET /schemas?group_id=). ONE CELL A SCHEMA: its newest version (group_by_uri),
 *  the older ones in its inspector, and every page of them, not the first 200. */
export function useSchemas(group) {
    const [schemas, setSchemas] = useState([]);
    const [error, setError] = useState(null);
    const load = useCallback(async () => {
        try {
            const all = [];
            for (let offset = 0; ; offset += SCHEMA_PAGE) {
                const r = await api.request(`/schemas?include_json_schema=true&include_apps=true&include_extended_by=true&group_by_uri=true&limit=${SCHEMA_PAGE}&offset=${offset}${group ? `&group_id=${encodeURIComponent(group)}` : ''}`);
                const rows = (r?.schemas || []).filter((s) => s.id);
                all.push(...rows);
                if (rows.length < SCHEMA_PAGE || all.length >= (typeof r?.total === 'number' ? r.total : all.length)) break;
            }
            setSchemas(all.map(schemaRowOf).sort((a, b) => a.name.localeCompare(b.name)));
            setError(null);
        } catch (e) { setError(e?.message || 'Schemas didn’t load.'); }
    }, [group]);
    useEffect(() => { load(); }, [load]);
    return {
        schemas, error, load,
        validate: async (s, sample) => {
            let obj;
            try { obj = JSON.parse(sample); } catch { return 'The sample isn’t JSON.'; }
            if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return 'The sample must be an object.';
            try {
                const j = await api.validateSchema(s.id, { metadata: obj });
                if (j?.valid) return 'Valid';
                return (j?.errors || []).map(String).join('\n') || 'Not valid';
            } catch (e) { return e?.message || 'Not valid'; }
        },
        versions: async (s) => {
            try {
                const j = await api.request(`/schemas/name/${encodeURIComponent(s.name)}/versions?include_json_schema=true`);
                // newest first, as the server sends them: the first is the one in use
                return (j?.versions || []).filter((v) => v.id).map((v) => ({ id: v.id, version: String(v.version ?? '?'), json: JSON.stringify(v.json_schema || {}, null, 2) }));
            } catch { return []; }
        },
    };
}

/** New schemas belong to the space you are in: a group's carry its group_id. */
export const schemaApi = {
    create: (group, body) => api.createSchema(group && !body.group_id ? { ...body, group_id: group } : body),
    version: (s, body) => api.updateSchema(s.id, body),
    remove: (s) => api.deleteSchema(s.id),
    transfer: (s, body) => api.request(`/schemas/${s.id}/transfer`, { method: 'POST', body }),
};

/** Is the window narrow (a phone)? The corner then folds its secondary verbs into one menu. */
export function useNarrow(px = 760) {
    const q = `(max-width: ${px}px)`;
    const [narrow, setNarrow] = useState(() => (typeof window !== 'undefined' && !!window.matchMedia?.(q).matches));
    useEffect(() => {
        const m = typeof window !== 'undefined' ? window.matchMedia?.(q) : null;
        if (!m) return undefined;
        const on = () => setNarrow(m.matches);
        on();
        m.addEventListener?.('change', on);
        return () => m.removeEventListener?.('change', on);
    }, [q]);
    return narrow;
}
